//frontend/src/components/delivery/DeliveredOrders.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
    Calendar,
    Clock,
    MapPin,
    Package,
    User,
    Phone,
    IndianRupee,
    Loader2,
    ShoppingBag,
    CheckCircle2,
    FileDown,
    Filter,
    Search,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import axios from 'axios';

// Order Card Component for better separation of concerns
const OrderCard = ({ order, getAreaName }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-200 hover:border-[#dc2626]/20 transition-colors duration-200 shadow-sm">
            {/* Order Header - Always visible */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900 font-['Crimson_Text']">
                        Order #{order.orderId}
                    </h3>
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Delivered
                    </span>
                </div>
                
                {/* Mobile toggle button */}
                <button 
                    className="flex items-center text-gray-500 sm:hidden" 
                    onClick={() => setExpanded(!expanded)}
                >
                    <span className="mr-1 text-xs">{expanded ? 'Hide Details' : 'View Details'}</span>
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>
            
            {/* Order Content - Always visible on desktop, togglable on mobile */}
            <div className={`${expanded ? 'block' : 'hidden'} sm:block`}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5">
                    {/* Order Info */}
                    <div>
                        <div className="space-y-2">
                            <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                                {new Date(order.deliveredAt).toLocaleDateString('en-IN', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                                <Clock className="w-3.5 h-3.5 mr-1.5" />
                                {new Date(order.deliveredAt).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-2 mt-3 sm:mt-0">
                        <div className="flex items-start gap-2">
                            <User className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Customer</p>
                                <p className="font-medium text-sm text-gray-900">{order.user.name}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Phone</p>
                                <p className="font-medium text-sm text-gray-900">{order.shippingInfo.phoneno}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Delivery Address</p>
                                <p className="font-medium text-sm text-gray-900">
                                    {order.shippingInfo.address},
                                    {getAreaName(order.shippingInfo.postalCode)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Order Items & Amount */}
                    <div className="mt-3 sm:mt-0">
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-500 mb-2 flex items-center">
                                    <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                                    Order Items
                                </p>
                                <div className="space-y-2">
                                    {order.orderItems.map((item, index) => (
                                        <div 
                                            key={index} 
                                            className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg"
                                        >
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded-md"
                                            />
                                            <div>
                                                <p className="font-medium text-xs sm:text-sm text-gray-900">{item.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    Qty: {item.quantity}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-gray-200">
                                <div className="flex flex-col space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">Items Total:</span>
                                        <span className="font-medium">₹{order.itemsPrice.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">Delivery:</span>
                                        <span className="font-medium">₹{order.shippingPrice.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t pt-2 mt-1">
                                        <div className="flex justify-between">
                                            <span className="font-medium text-xs text-gray-900">Total:</span>
                                            <span className="font-bold text-sm text-[#dc2626]">₹{order.totalPrice.toFixed(2)}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Payment: {order.paymentInfo.paymentMethod}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Summary Card Component
const SummaryCard = ({ filteredOrders, filterType }) => {
    const calculateTotalAmount = () => {
        return filteredOrders.reduce((total, order) => total + order.totalPrice, 0);
    };
    
    return (
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div>
                    <p className="text-xs text-gray-500">Total Orders</p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">{filteredOrders.length}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Total Amount</p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">₹{calculateTotalAmount().toFixed(2)}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Avg Order Value</p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">
                        ₹{filteredOrders.length ? (calculateTotalAmount() / filteredOrders.length).toFixed(2) : '0.00'}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Time Period</p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900 capitalize">{filterType}</p>
                </div>
            </div>
        </div>
    );
};

const DeliveredOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'today', 'week', 'month'
    const { deliveryStaff } = useSelector(state => state.deliveryStaffState);

    useEffect(() => {
        fetchDeliveredOrders();
    }, [filterType]);

    const fetchDeliveredOrders = async () => {
        try {
            setLoading(true);
            const params = {};

            if (filterType !== 'all') {
                const today = new Date();
                let startDate = new Date();

                switch (filterType) {
                    case 'today':
                        startDate.setHours(0, 0, 0, 0);
                        break;
                    case 'week':
                        startDate.setDate(today.getDate() - 7);
                        break;
                    case 'month':
                        startDate.setMonth(today.getMonth() - 1);
                        break;
                    default:
                        break;
                }

                params.startDate = startDate.toISOString();
                params.endDate = today.toISOString();
            }

            const { data } = await axios.get('/api/v1/delivery/orders/delivered', {
                params,
                withCredentials: true
            });

            if (data.success) {
                setOrders(data.orders);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching delivered orders');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!startDate || !endDate) {
            toast.error('Please select both start and end dates');
            return;
        }

        setDownloadingReport(true);
        try {
            const response = await axios.get('/api/v1/delivery/orders/report', {
                params: { startDate, endDate },
                responseType: 'blob',
                withCredentials: true
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `delivered-orders-${startDate}-to-${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Report downloaded successfully');
        } catch (error) {
            toast.error('Error downloading report');
        } finally {
            setDownloadingReport(false);
        }
    };

    const getAreaName = (pincode) => {
        const areas = {
            '562130': 'Gollarahatti',
            '562130': 'Kadabagere',
            '560112': 'Chikka Gollarahatti',
            '560091': 'Machohalli',
            '560091': 'Anjana Nagar',
            '560091': 'Byadarahalli',
            '562130': 'Tavarekere',
            '560091': 'Herohalli',
            '560091': 'Viswaneedam',
            '560091': 'Sunkadakatte',
            '560091': 'Muddayanapalya',
            '560091': 'Annapurneshwari Nagar',
            '560091': 'Kottigepalya',
            '560091': 'Sumanahalli'
        };
        return areas[pincode] || pincode;
    };

    const filteredOrders = orders.filter(order => {
        const searchString = searchTerm.toLowerCase();
        return (
            order.orderId.toLowerCase().includes(searchString) ||
            order.user.name.toLowerCase().includes(searchString) ||
            getAreaName(order.shippingInfo.postalCode).toLowerCase().includes(searchString)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-10 h-10 animate-spin text-[#dc2626]" />
            </div>
        );
    }

    return (
        <div 
            className="min-h-screen py-4 sm:py-6"
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="bg-white bg-opacity-95 rounded-lg shadow-sm overflow-hidden">
                    {/* Header Section */}
                    <div className="p-3 sm:p-5 border-b border-gray-200">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4">
                            <div>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#dc2626]" />
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-bold text-red-600 font-['Crimson_Text']">
                                            Delivered Orders
                                        </h2>
                                        <p className="text-xs sm:text-sm text-gray-500">
                                            {deliveryStaff?.name}'s Delivery History
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                                <div className="relative flex-grow sm:flex-grow-0">
                                    <input
                                        type="text"
                                        placeholder="Search orders..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                    />
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                                </div>

                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="w-full sm:w-auto pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                >
                                    <option value="all">All Time</option>
                                    <option value="today">Today</option>
                                    <option value="week">Last 7 Days</option>
                                    <option value="month">Last 30 Days</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Report Download Section */}
                    <div className="p-3 sm:p-5 border-b border-gray-200 bg-gray-50/70">
                        <div className="max-w-3xl mx-auto">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 font-['Crimson_Text']">Download Delivery Report</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-[#dc2626] focus:border-[#dc2626]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-[#dc2626] focus:border-[#dc2626]"
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={handleDownloadReport}
                                        disabled={downloadingReport || !startDate || !endDate}
                                        className="w-full bg-[#dc2626] text-white px-3 py-1.5 rounded-lg hover:bg-[#dc2626]/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                                    >
                                        {downloadingReport ? (
                                            <>
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                <span className="hidden sm:inline">Downloading...</span>
                                                <span className="sm:hidden">Download...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FileDown className="w-3.5 h-3.5" />
                                                <span className="hidden sm:inline">Download Report</span>
                                                <span className="sm:hidden">Download</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Orders List */}
                    <div className="p-3 sm:p-5">
                        {filteredOrders.length > 0 ? (
                            <div className="space-y-3 sm:space-y-5">
                                {/* Summary Card */}
                                <SummaryCard 
                                    filteredOrders={filteredOrders}
                                    filterType={filterType}
                                />

                                {/* Orders Grid */}
                                <div className="grid gap-3 sm:gap-5">
                                    {filteredOrders.map((order) => (
                                        <OrderCard 
                                            key={order._id}
                                            order={order}
                                            getAreaName={getAreaName}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 sm:py-10">
                                <Package className="w-12 h-12 sm:w-14 sm:h-14 mx-auto text-gray-400 mb-2 sm:mb-3" />
                                <h3 className="text-lg font-medium text-gray-900 mb-1 sm:mb-2 font-['Crimson_Text']">No Delivered Orders</h3>
                                <p className="text-xs sm:text-sm text-gray-500">
                                    {searchTerm 
                                        ? 'No orders match your search criteria.' 
                                        : 'You haven\'t delivered any orders yet.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveredOrders;